import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../../api/axios";
import toast from "react-hot-toast";
import QuillEditor from "../../common/QuillEditor";
import { Save, ArrowLeft, Loader2, ImagePlus } from "lucide-react";
import { useSettings } from "../../../context/SettingsContext";

export default function AddEmployeePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { settings } = useSettings();
  const [loading, setLoading] = useState(!!slug);
  const [saving, setSaving] = useState(false);
  const [employeeId, setEmployeeId] = useState(null);

  const [formData, setFormData] = useState({
    fullName: "",
    titleTr: "",
    titleEn: "",
    titleAr: "",
    phone: "",
    email: "",
    linkedInUrl: "",
    bioTr: "",
    bioEn: "",
    bioAr: "",
    descriptionTr: "",
    descriptionEn: "",
    descriptionAr: "",
    sortOrder: 0,
    isActive: true,
    image: null,
    imagePreview: "",
  });

  useEffect(() => {
    if (!slug) return;
    (async () => {
      try {
        const res = await api.get(`/employee/${slug}`);
        const d = res.data;
        setEmployeeId(d.employeeId);
        setFormData({
          fullName: d.fullName || "",
          titleTr: d.titleTr || "",
          titleEn: d.titleEn || "",
          titleAr: d.titleAr || "",
          phone: d.phone || "",
          email: d.email || "",
          linkedInUrl: d.linkedInUrl || "",
          bioTr: d.bioTr || "",
          bioEn: d.bioEn || "",
          bioAr: d.bioAr || "",
          descriptionTr: d.descriptionTr || "",
          descriptionEn: d.descriptionEn || "",
          descriptionAr: d.descriptionAr || "",
          sortOrder: d.sortOrder || 0,
          isActive: d.isActive !== undefined ? d.isActive : true,
          image: null,
          imagePreview: d.imagePath
            ? `${process.env.REACT_APP_UPLOAD_URL}/${d.imagePath}`
            : "",
        });
      } catch {
        toast.error("Çalışan bilgileri yüklenemedi");
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    if (type === "file" && files[0]) {
      const reader = new FileReader();
      reader.onload = (ev) =>
        setFormData((p) => ({
          ...p,
          image: files[0],
          imagePreview: ev.target.result,
        }));
      reader.readAsDataURL(files[0]);
    } else {
      setFormData((p) => ({ ...p, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.fullName.trim()) return toast.error("Ad soyad gerekli");
    setSaving(true);

    const data = new FormData();
    data.append("FullName", formData.fullName);
    data.append("TitleTr", formData.titleTr);
    data.append("TitleEn", formData.titleEn);
    data.append("TitleAr", formData.titleAr);
    data.append("Phone", formData.phone);
    data.append("Email", formData.email);
    data.append("LinkedInUrl", formData.linkedInUrl);
    data.append("BioTr", formData.bioTr);
    data.append("BioEn", formData.bioEn);
    data.append("BioAr", formData.bioAr);
    data.append("DescriptionTr", formData.descriptionTr);
    data.append("DescriptionEn", formData.descriptionEn);
    data.append("DescriptionAr", formData.descriptionAr);
    data.append("SortOrder", formData.sortOrder);
    data.append("IsActive", formData.isActive);
    if (formData.image) data.append("image", formData.image);

    try {
      if (slug) {
        await api.put(`/employee/${employeeId}`, data, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Çalışan güncellendi");
      } else {
        await api.post("/employee", data, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Çalışan eklendi");
      }
      navigate("/employee-list");
    } catch {
      toast.error("Çalışan kaydedilirken hata oluştu");
    } finally {
      setSaving(false);
    }
  };

  if (loading)
    return (
      <div className="page-loading">
        <div className="loading-spinner" />
        <p>Yükleniyor...</p>
      </div>
    );

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h2>{slug ? "Çalışan Düzenle" : "Yeni Çalışan Ekle"}</h2>
          <p className="page-subtitle">
            {slug
              ? "Çalışan bilgilerini düzenleyin"
              : "Yeni bir ekip üyesi ekleyin"}
          </p>
        </div>
        <button
          className="btn btn-ghost"
          onClick={() => navigate("/employee-list")}
        >
          <ArrowLeft size={18} /> Listeye Dön
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="form-layout-2col">
          <div className="form-main">
            <div className="content-card">
              <h3 className="card-title">Genel Bilgiler</h3>
              <div className="form-field">
                <label>Ad Soyad</label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Ad Soyad"
                />
              </div>
              <div className="form-field">
                <label>Telefon</label>
                <input
                  type="text"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Telefon numarası"
                />
              </div>
              <div className="form-field">
                <label>E-posta</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="E-posta adresi"
                />
              </div>
              <div className="form-field">
                <label>LinkedIn URL</label>
                <input
                  type="url"
                  name="linkedInUrl"
                  value={formData.linkedInUrl}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="https://linkedin.com/in/..."
                />
              </div>
            </div>

            {(!settings || settings.turkish) && (
              <div className="content-card">
                <h3 className="card-title">🇹🇷 Türkçe İçerik</h3>
                <div className="form-field">
                  <label>Unvan</label>
                  <input
                    type="text"
                    name="titleTr"
                    value={formData.titleTr}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="Örn: Yazılım Mühendisi"
                  />
                </div>
                <div className="form-field">
                  <label>Biyografi</label>
                  <QuillEditor
                    value={formData.bioTr}
                    onChange={(c) => setFormData((p) => ({ ...p, bioTr: c }))}
                    placeholder="Çalışan hakkında bilgi..."
                  />
                </div>
                <div className="form-field">
                  <label>Açıklama</label>
                  <QuillEditor
                    value={formData.descriptionTr}
                    onChange={(c) => setFormData((p) => ({ ...p, descriptionTr: c }))}
                    placeholder="Detaylı açıklama..."
                  />
                </div>
              </div>
            )}

            {settings?.english && (
              <div className="content-card">
                <h3 className="card-title">🇬🇧 English Content</h3>
                <div className="form-field">
                  <label>Title</label>
                  <input
                    type="text"
                    name="titleEn"
                    value={formData.titleEn}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="e.g. Software Engineer"
                  />
                </div>
                <div className="form-field">
                  <label>Bio</label>
                  <QuillEditor
                    value={formData.bioEn}
                    onChange={(c) => setFormData((p) => ({ ...p, bioEn: c }))}
                    placeholder="About the employee..."
                  />
                </div>
                <div className="form-field">
                  <label>Description</label>
                  <QuillEditor
                    value={formData.descriptionEn}
                    onChange={(c) => setFormData((p) => ({ ...p, descriptionEn: c }))}
                    placeholder="Detailed description..."
                  />
                </div>
              </div>
            )}

            {settings?.arabic && (
              <div className="content-card">
                <h3 className="card-title">🇸🇦 المحتوى العربي</h3>
                <div className="form-field">
                  <label>المسمى الوظيفي</label>
                  <input
                    type="text"
                    name="titleAr"
                    value={formData.titleAr}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="المسمى الوظيفي"
                  />
                </div>
                <div className="form-field">
                  <label>السيرة الذاتية</label>
                  <QuillEditor
                    value={formData.bioAr}
                    onChange={(c) => setFormData((p) => ({ ...p, bioAr: c }))}
                    placeholder="نبذة عن الموظف..."
                    dir="rtl"
                  />
                </div>
                <div className="form-field">
                  <label>الوصف</label>
                  <QuillEditor
                    value={formData.descriptionAr}
                    onChange={(c) => setFormData((p) => ({ ...p, descriptionAr: c }))}
                    placeholder="وصف تفصيلي..."
                    dir="rtl"
                  />
                </div>
              </div>
            )}
          </div>

          <div className="form-sidebar">
            <div className="content-card sticky-card">
              <h3 className="card-title">Profil Görseli</h3>
              <div className="image-upload-area">
                {formData.imagePreview ? (
                  <div className="image-preview">
                    <img src={formData.imagePreview} alt="Preview" />
                    <button
                      type="button"
                      className="remove-image"
                      onClick={() =>
                        setFormData((p) => ({
                          ...p,
                          image: null,
                          imagePreview: "",
                        }))
                      }
                    >
                      ×
                    </button>
                  </div>
                ) : (
                  <label className="upload-placeholder">
                    <ImagePlus size={32} />
                    <span>Görsel Yükle</span>
                    <input
                      type="file"
                      name="image"
                      accept="image/*"
                      onChange={handleChange}
                      hidden
                    />
                  </label>
                )}
              </div>
            </div>

            <div className="content-card">
              <h3 className="card-title">Ayarlar</h3>
              <div className="form-field">
                <label>Sıralama</label>
                <input
                  type="number"
                  name="sortOrder"
                  value={formData.sortOrder}
                  onChange={handleChange}
                  className="form-input"
                  min={0}
                />
              </div>
              <div className="form-field">
                <label className="toggle-label">
                  <span>Aktif</span>
                  <label className="toggle">
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={formData.isActive}
                      onChange={(e) =>
                        setFormData({ ...formData, isActive: e.target.checked })
                      }
                    />
                    <span className="toggle-slider" />
                  </label>
                </label>
              </div>
            </div>

            <button
              type="submit"
              className="btn btn-primary btn-full"
              disabled={saving}
            >
              {saving ? (
                <>
                  <Loader2 size={18} className="spin" /> Kaydediliyor...
                </>
              ) : (
                <>
                  <Save size={18} /> {slug ? "Güncelle" : "Kaydet"}
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
